import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Download, Plus, Trash2, Upload } from "lucide-react";
import { useFacility } from "../../../context/FacilityContext";
import { Avatar, BulkBar, ConfirmDialog, DataTable, EmptyRow, FilterSelect, SearchBox, StatusBadge, UndoToast } from "../../components/crm/ui";
import {
  CONTACT_STATUSES, CONTACT_TYPES, Checkbox, ColumnPicker, PAGE_SIZES, PRIORITIES, PRIORITY_KEY, Pagination, RowActions, SOURCES, SOURCE_KEY, SourceLabel,
  apiCall, apiGet, contactTypeKey, downloadFile, fmtPhone, fullName, initials, inputCls, leadsOfContact, leadsOfContacts, toContact, toLead, useColumns, usePeople, useSavedView,
} from "../../components/crm/contactsKit";
import { VIEW_ONLY_TITLE, ViewOnlyNote, useCrmAccess } from "../../components/crm/crmAccess";
import { AccessNotice, fetchApi, reasonOf } from "../../components/marketing/access";
import ImportDialog from "../../components/crm/ImportDialog";
import { PrimaryButton } from "../../components/ui";
import { InlineLoader, PageLoader } from "../../components/PageLoader";
import { SearchSelect } from "../../components/SearchSelect";
import { shortName } from "../../components/FacilitySwitcher";
import { fmtAgo, NO_DATA } from "../../lib/format";

/**
 * CRM → Contacts (design: screens/crm/Contacts.tsx).
 *
 * Real data and actions: contacts with search, type, status, tag and "Leads"
 * (has_leads, item 154) filters, sorting by column (sort_by / sort_order, 156)
 * and paging, at the selected location (facility_id, 155) — GET /api/contacts;
 * each row's lead count and latest lead (status, source, location) come with
 * the row (154). The totals and "N linked to leads" (GET /api/contacts/stats),
 * tags (GET /api/contacts/tags), delete and bulk delete with Undo
 * (POST /api/contacts/:id/restore, 158; bulk delete is clinic admins only),
 * "Create leads" for selected contacts (POST /api/leads each, at the selected
 * location; the open-lead check is one GET /api/leads?contact_id=a,b,c, 153),
 * import (ImportDialog), and the columns, sort and page size saved for the
 * user (GET/PUT /api/view-preferences/contacts_table, 170).
 * Export CSV: GET /api/contacts/bulk/export, for the ticked contacts (ids) or
 * for every contact matching the filters. "Select all N matching" ticks every
 * match, not just the page; then Delete is "Delete all matching", which
 * previews the count and what goes with it (POST /api/contacts/bulk/preview-delete,
 * mode all_matching_filter) and needs DELETE typed (POST /api/contacts/bulk/delete-all).
 * Both bulk deletes are for clinic owners and admins only: GET /api/contacts/bulk/me
 * (useCrmAccess) hides them for everyone else. "Matching" sends the list's own
 * filters (search, type, status, tag, has_leads, facility_id), which the bulk
 * preview and delete match exactly. The CSV export (GET /bulk/export) still
 * reads only search, type, status and tag, so with the Leads or location filter
 * on, exporting every match is off and says why (selected rows still export).
 * View only (staff CRM permission): import, add, edit, delete and create leads are off.
 */

/** Design column id → GET /api/contacts sort_by (config/contactColumns.js). */
const SORT_KEY = { name: "name", phone: "phone", email: "email", type: "contact_type", status: "status", leads: "lead_count", company: "company_name", jobTitle: null, city: "city", created: "created_at" };
const SORT_COLUMN = Object.fromEntries(Object.entries(SORT_KEY).filter(([, v]) => v).map(([k, v]) => [v, k]));

function LeadCreatedToast({ count, onView, onDone }) {
  useEffect(() => {
    const timeout = window.setTimeout(onDone, 6000);
    return () => window.clearTimeout(timeout);
  }, [onDone]);
  return createPortal(
    <div className="olli pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center font-sans" role="status">
      <div className="pointer-events-auto flex h-12 animate-nav-in items-center gap-3 rounded-full bg-ink pl-5 pr-2 text-small font-medium text-paper shadow-lift">
        {count} leads created
        <button type="button" onClick={onView} className="flex h-9 items-center rounded-full bg-paper/10 px-4 font-semibold text-paper hover:bg-paper/20">View leads</button>
      </div>
    </div>,
    document.body,
  );
}

const UNASSIGNED = "Unassigned (unless a rule matches)";
const RULES = "Use assignment rules";

export default function Contacts() {
  const navigate = useNavigate();
  const location = useLocation();
  const fac = useFacility();
  const people = usePeople();
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [tag, setTag] = useState("");
  const [hasLeads, setHasLeads] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statsState, setStatsState] = useState({ for: undefined, value: null }); // stats and the location they were loaded for
  const [loadedKey, setLoadedKey] = useState(null); // the filters and page the rows on screen were loaded for
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [tags, setTags] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [openLeadsBy, setOpenLeadsBy] = useState(null);
  const [confirmLeads, setConfirmLeads] = useState(null);
  const view = useSavedView("contacts_table");
  const [sort, setSort] = useState(null);
  const [ready, setReady] = useState(false);
  const facilityId = fac.apiParams().facility_id || null;
  const facilityLabel = fac.currentFacility ? fac.currentFacility.name : "All locations";
  const [confirming, setConfirming] = useState(null);
  const [bulkDelete, setBulkDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [convertOpen, setConvertOpen] = useState(false);
  const [skipOpenLeads, setSkipOpenLeads] = useState(true);
  const [source, setSource] = useState("Manual entry");
  const [leadStatus, setLeadStatus] = useState("new");
  const [service, setService] = useState("");
  const [assignee, setAssignee] = useState(UNASSIGNED);
  const [priority, setPriority] = useState("Medium");
  const [creating, setCreating] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);
  const [createdToast, setCreatedToast] = useState(false);
  const [listError, setListError] = useState(null); // a refused or failed list load
  const [allMatching, setAllMatching] = useState(false); // every contact matching the filters is selected
  const [deleteAll, setDeleteAll] = useState(null); // { count, cascade } | { loading } | { error }
  const [exporting, setExporting] = useState(false);
  const access = useCrmAccess();
  const canEdit = access.canEdit;
  const canBulkDelete = canEdit && access.isOrgAdmin !== false;
  const tableRef = useRef(null);

  // Search waits for a pause in typing.
  useEffect(() => { const t = setTimeout(() => setSearch(query.trim()), 300); return () => clearTimeout(t); }, [query]);
  useEffect(() => { setPage(1); }, [search, type, status, tag, hasLeads, size, sort, facilityId]);
  // The saved sort and page size, once (item 170).
  useEffect(() => {
    if (!view.loaded) return;
    const pref = view.pref;
    if (pref && !pref.is_default) {
      const col = SORT_COLUMN[pref.sort_by];
      if (col && !(pref.sort_by === "created_at" && pref.sort_order === "DESC")) setSort({ id: col, dir: pref.sort_order === "ASC" ? 1 : -1 });
      if (PAGE_SIZES.includes(Number(pref.page_size))) setSize(Number(pref.page_size));
    }
    setReady(true);
  }, [view.loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeSort = (next) => { setSort(next); view.save({ sort_by: next ? SORT_KEY[next.id] : "created_at", sort_order: next?.dir === 1 ? "ASC" : "DESC" }); };
  const changeSize = (next) => { setSize(next); view.save({ page_size: next }); };

  // Late answers for an older location or filter are dropped (latest.current).
  const latest = useRef({});
  latest.current.facilityId = facilityId;
  const loadStats = useCallback(() => apiGet("/api/contacts/stats", facilityId ? { facility_id: facilityId } : {}).then((r) => {
    if (latest.current.facilityId !== facilityId) return;
    setStatsState({ for: facilityId, value: r?.success ? { total: Number(r.data?.total || 0), linked: Number(r.data?.linked_to_leads || 0) } : null });
  }), [facilityId]);
  const statsLoading = statsState.for !== facilityId;
  const stats = statsLoading ? null : statsState.value;
  const allTotal = stats ? stats.total : null;
  const tagId = tags.find((t) => t.name === tag)?.id;
  const listKey = JSON.stringify({ page, size, search, type, status, tagId, hasLeads, facilityId, sort });
  latest.current.listKey = listKey;
  // Until the rows for the current filters and page arrive, the table shows the
  // loader (a refresh on the same filters keeps the rows on screen).
  const loading = !ready || loadedKey !== listKey;
  const load = useCallback(async () => {
    const key = listKey;
    const res = await fetchApi("/api/contacts", { params: {
      page, limit: size,
      ...(search ? { search } : {}), ...(type ? { contact_type: contactTypeKey(type) } : {}), ...(status ? { status: status.toLowerCase() } : {}), ...(tagId ? { tag_id: tagId } : {}),
      ...(hasLeads ? { has_leads: hasLeads === "With leads" ? "yes" : "no" } : {}),
      ...(facilityId ? { facility_id: facilityId } : {}),
      ...(sort && SORT_KEY[sort.id] ? { sort_by: SORT_KEY[sort.id], sort_order: sort.dir === 1 ? "ASC" : "DESC" } : {}),
    } });
    if (latest.current.listKey !== key) return;
    setListError(res.ok ? null : reasonOf(res));
    setRows(res.ok ? (res.data?.data || []).map(toContact) : []);
    setTotal(res.ok ? Number(res.data?.pagination?.total || 0) : 0);
    setLoadedKey(key);
  }, [page, size, search, type, status, tagId, hasLeads, facilityId, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  // Wait for the saved view so the first page comes back in the saved order.
  useEffect(() => { if (ready) load(); }, [load, ready]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    apiGet("/api/contacts/tags").then((r) => setTags(r?.success ? r.data || [] : []));
    apiGet("/api/lead-statuses").then((r) => setLeadStatuses(r?.success ? (r.data || []).filter((s) => s.is_active !== 0) : []));
  }, []);

  const statusLabels = useMemo(() => Object.fromEntries(leadStatuses.map((s) => [s.key_name, s.label])), [leadStatuses]);
  // The row's lead summary (item 154): count and latest lead, null when unknown.
  const latestOf = useCallback((c) => (c.latestLead ? toLead(c.latestLead, statusLabels) : null), [statusLabels]);

  const allColumns = useMemo(() => [
    { id: "name", label: "Name", locked: true, default: true, width: "minmax(230px,1.5fr)", render: (c) => <span className="flex min-w-0 items-center gap-3"><Avatar name={fullName(c)} initials={initials(c)} size="sm" /><span className="flex min-w-0 flex-col"><span className="truncate font-semibold">{[c.title, fullName(c)].filter(Boolean).join(" ")}</span><span className="truncate text-caption text-muted">{c.email || "No email added"}</span></span></span> },
    { id: "phone", label: "Phone", default: true, render: (c) => fmtPhone(c.phone || c.rawPhone) },
    { id: "email", label: "Email", render: (c) => c.email },
    { id: "altPhone", label: "Alternate phone", render: (c) => (c.altPhone ? fmtPhone(c.altPhone) : "") },
    { id: "type", label: "Type", render: (c) => c.type },
    { id: "status", label: "Status", render: (c) => <span className={`inline-flex items-center gap-2 ${c.status === "Inactive" ? "text-muted" : "text-ink"}`}><span className={`h-1.5 w-1.5 rounded-full ${c.status === "Active" ? "bg-success" : "bg-muted"}`} />{c.status}</span> },
    { id: "leads", label: "Leads", default: true, render: (c) => { if (c.leadCount === null) return NO_DATA; const latest = latestOf(c); return <span className="flex items-center gap-2"><span className="tabular-nums">{c.leadCount}</span>{latest && <StatusBadge status={latest.status} />}</span>; } },
    { id: "source", label: "Latest source", default: true, render: (c) => { if (c.leadCount === null) return NO_DATA; const latest = latestOf(c); return latest ? <SourceLabel source={latest.source} /> : <span className="text-muted">No lead yet</span>; } },
    { id: "company", label: "Company", render: (c) => c.company },
    { id: "jobTitle", label: "Job title", render: (c) => c.jobTitle },
    { id: "city", label: "City", default: true, render: (c) => c.address.city },
    { id: "area", label: "Area", render: (c) => c.address.line2 },
    { id: "tags", label: "Tags", default: true, render: (c) => <span className="flex flex-wrap gap-1">{c.tags.map((t) => <span key={t} className="rounded-full bg-mist px-2 py-1 text-caption">{t}</span>)}</span> },
    { id: "facility", label: "Location", default: true, render: (c) => { if (c.leadCount === null) return NO_DATA; const latest = latestOf(c); return latest ? (latest.facility ? shortName(latest.facility) : "Not set") : <span className="text-muted">No lead yet</span>; } },
    { id: "created", label: "Created", default: true, render: (c) => (c.createdAt ? fmtAgo(c.createdAt) : NO_DATA) },
    { id: "createdBy", label: "Created by", render: (c) => c.createdBy },
  ].map((c) => (SORT_KEY[c.id] ? { ...c, sort: true } : c)), [latestOf]);
  const cols = useColumns(allColumns, "olli.crm.contacts.columns", { ids: view.pref?.visible_columns, save: (ids) => view.save({ visible_columns: ids }) });

  const pg = { page, pages: Math.max(1, Math.ceil(total / size)), size, start: (page - 1) * size, end: Math.min(total, page * size), total, setPage, setSize: changeSize };
  const clear = () => { setType(""); setStatus(""); setTag(""); setHasLeads(""); setQuery(""); };
  const selectedContacts = rows.filter((contact) => selected.has(contact.id));
  // Open leads of the selected contacts, fetched in one request when "Create leads" opens (item 153).
  const hasOpenLead = (contact) => (openLeadsBy?.[contact.id] || []).some((lead) => toLead(lead).open);
  const selectedOpenLeadCount = selectedContacts.filter(hasOpenLead).length;
  const contactsToConvert = selectedContacts.filter((contact) => !skipOpenLeads || !hasOpenLead(contact));
  const crmEmpty = allTotal === 0;
  // "All matching" (preview, delete all, export): the same filters as the list.
  const serverFilters = {
    ...(search ? { search } : {}), ...(type ? { contact_type: contactTypeKey(type) } : {}), ...(status ? { status: status.toLowerCase() } : {}), ...(tagId ? { tag_id: tagId } : {}),
    ...(hasLeads ? { has_leads: hasLeads === "With leads" ? "yes" : "no" } : {}),
    ...(facilityId ? { facility_id: facilityId } : {}),
  };
  // A new filter starts a new selection.
  useEffect(() => { setAllMatching(false); }, [search, type, status, tagId, hasLeads, facilityId]);
  const changeSelection = (next) => { setSelected(next); if (allMatching) setAllMatching(false); };
  const clearSelection = () => { setSelected(new Set()); setAllMatching(false); };

  useEffect(() => {
    const visible = new Set(rows.map((contact) => contact.id));
    if (allMatching) { setSelected(visible); return; }
    setSelected((current) => {
      const next = new Set([...current].filter((id) => visible.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (selected.size) { setSelected(new Set()); setAllMatching(false); }
      if (convertOpen) setConvertOpen(false);
      if (bulkDelete) setBulkDelete(null);
      if (confirming) setConfirming(null);
      if (importOpen) setImportOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected.size, convertOpen, bulkDelete, confirming, importOpen]);

  const refresh = () => { load(); loadStats(); };
  // Undo a delete (item 158): restore brings back the contact and everything its delete removed.
  const undoDelete = (ids) => async () => {
    const results = await Promise.allSettled(ids.map((id) => apiCall("post", `/api/contacts/${id}/restore`)));
    const failed = results.find((r) => r.status === "rejected");
    if (failed) setToast({ message: failed.reason?.message || "Couldn't restore" });
    refresh();
  };
  // A delete from the contact's own page lands here with its Undo (item 158).
  const deletedHere = location.state?.deletedContact?.id;
  useEffect(() => {
    if (!deletedHere) return;
    setToast({ message: "Contact deleted", onUndo: undoDelete([deletedHere]) });
    navigate(location.pathname, { replace: true, state: null });
  }, [deletedHere]); // eslint-disable-line react-hooks/exhaustive-deps
  const openConvert = async () => {
    setSkipOpenLeads(true);
    setOpenLeadsBy(null);
    setConvertOpen(true);
    setOpenLeadsBy(await leadsOfContacts(selectedContacts.map((c) => c.id)) || {});
  };
  const openConfirm = async (contact) => {
    setConfirmLeads(null);
    setConfirming(contact);
    const list = contact.leadCount ? await leadsOfContact(contact) : [];
    setConfirmLeads(list ? list.map((l) => toLead(l, statusLabels)) : null);
  };
  const createLeads = async () => {
    setCreating(true);
    const person = people.find((p) => p.name === assignee);
    const facilityId = fac.apiParams().facility_id;
    const results = await Promise.allSettled(contactsToConvert.map((contact) => apiCall("post", "/api/leads", {
      contact_id: contact.id,
      first_name: contact.firstName,
      last_name: contact.lastName || null,
      email: contact.email || null,
      phone: contact.rawPhone || contact.phone || null,
      source: SOURCE_KEY[source] || "manual",
      status: leadStatus,
      priority: PRIORITY_KEY[priority],
      interested_service: service || null,
      ...(person ? { assigned_to: person.id } : {}),
      ...(facilityId ? { facility_id: facilityId } : {}),
    })));
    const added = results.filter((r) => r.status === "fulfilled").length;
    setCreating(false);
    setConvertOpen(false);
    setSelected(new Set());
    if (added) { setCreatedCount(added); setCreatedToast(true); }
    if (added < results.length) setToast({ message: `${results.length - added} leads could not be created` });
    load();
  };
  const openBulkDelete = async () => {
    const ids = selectedContacts.map((c) => c.id);
    const r = await apiCall("post", "/api/contacts/bulk/preview-delete", { mode: "explicit_ids", ids }).catch(() => null);
    setBulkDelete({ ids, leads: r?.success ? Number(r.data?.cascade?.leads || 0) : null });
  };
  // Export CSV (old ContactsList): the ticked contacts, or every match.
  const exportContacts = async (scope) => {
    setExporting(true);
    try {
      await downloadFile("/api/contacts/bulk/export", scope === "selected" ? { ids: selectedContacts.map((c) => c.id).join(",") } : serverFilters, "contacts-export.csv");
    } catch (e) { setToast({ message: e.message }); }
    setExporting(false);
  };
  // Delete all matching: preview the count and the cascade, then type DELETE.
  const openDeleteAll = async () => {
    setDeleteAll({ loading: true });
    try {
      const r = await apiCall("post", "/api/contacts/bulk/preview-delete", { mode: "all_matching_filter", filters: serverFilters });
      setDeleteAll({ count: Number(r?.data?.contact_count || 0), cascade: r?.data?.cascade || {} });
    } catch (e) { setDeleteAll({ error: e.message }); }
  };
  const removeAllMatching = async () => {
    setDeleteAll((d) => ({ ...d, deleting: true }));
    try {
      const r = await apiCall("post", "/api/contacts/bulk/delete-all", { filters: serverFilters, confirmation_token: "DELETE" });
      setDeleteAll(null);
      clearSelection();
      setToast({ message: r?.message || "Contacts deleted" });
    } catch (e) { setDeleteAll((d) => ({ ...d, deleting: false, error: e.message })); }
    refresh();
  };
  const removeSelected = async () => {
    const { ids } = bulkDelete;
    setBulkDelete(null);
    try {
      await apiCall("post", "/api/contacts/bulk/delete", { mode: "explicit_ids", ids });
      setSelected(new Set());
      setToast({ message: `${ids.length} contacts deleted`, onUndo: undoDelete(ids) });
    } catch (e) { setToast({ message: e.message }); }
    refresh();
  };

  return (
    <div className="mx-auto flex max-w-7xl animate-fade-in flex-col gap-6 px-8 py-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="text-h1 font-semibold">Contacts</h1><p className="mt-1 text-small text-muted">{statsLoading ? <InlineLoader size={14} /> : allTotal === null ? NO_DATA : allTotal.toLocaleString("en-IN")} contacts · {statsLoading ? <InlineLoader size={14} /> : stats ? stats.linked.toLocaleString("en-IN") : NO_DATA} linked to leads · {facilityLabel}</p></div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => exportContacts("all")} disabled={exporting || !total || !!listError} className="flex h-11 items-center gap-2 rounded-full px-5 text-small font-semibold text-ink ring-1 ring-line hover:bg-mist disabled:cursor-not-allowed disabled:opacity-50"><Download size={16} strokeWidth={1.75} />{exporting ? "Exporting…" : "Export CSV"}</button>
          {canEdit && <button type="button" onClick={() => setImportOpen(true)} className="flex h-11 items-center gap-2 rounded-full px-5 text-small font-semibold text-ink ring-1 ring-line hover:bg-mist"><Upload size={16} strokeWidth={1.75} />Import</button>}
          <PrimaryButton disabled={!canEdit} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={() => navigate("/crm/contacts/new")}><Plus size={17} strokeWidth={1.75} />Add contact</PrimaryButton>
        </div>
      </header>
      {access.viewOnly && <ViewOnlyNote />}
      <div className="relative z-20 flex flex-wrap items-center gap-3">
        <FilterSelect label="Type" value={type} options={CONTACT_TYPES} onChange={setType} allLabel="All types" />
        <FilterSelect label="Status" value={status} options={CONTACT_STATUSES} onChange={setStatus} allLabel="All statuses" />
        <FilterSelect label="Tag" value={tag} options={tags.map((t) => t.name)} onChange={setTag} allLabel="All tags" />
        <FilterSelect label="Leads" value={hasLeads} options={["With leads", "No leads"]} onChange={setHasLeads} allLabel="All contacts" />
        <SearchBox value={query} onChange={setQuery} placeholder="Name, phone, email or company" className="ml-auto w-64" />
        <ColumnPicker all={allColumns} cols={cols} />
      </div>
      {!loading && rows.length > 0 && !listError && (allMatching || (selected.size === rows.length && total > rows.length)) && <div role="status" className="-mb-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-md bg-sky-soft px-4 py-2.5 text-small text-ink">
        {allMatching ? <>All {total.toLocaleString("en-IN")} matching contacts are selected.<button type="button" onClick={clearSelection} className="font-semibold text-sky-deep hover:underline">Clear selection</button></>
          : <>All {rows.length} contacts on this page are selected.<button type="button" onClick={() => setAllMatching(true)} className="font-semibold text-sky-deep hover:underline">Select all {total.toLocaleString("en-IN")} matching</button></>}
      </div>}
      {loading ? <PageLoader label="Loading contacts…" /> : listError ? <AccessNotice reason={listError} onRetry={() => { setLoadedKey(null); load(); }} /> : <>
        <div ref={tableRef}><DataTable
          columns={cols.visible}
          rows={rows}
          rowKey={(c) => c.id}
          onOpen={(c) => navigate(`/crm/contacts/${c.id}`)}
          actions={(c) => <RowActions label={fullName(c)} onView={() => navigate(`/crm/contacts/${c.id}`)} onEdit={canEdit ? () => navigate(`/crm/contacts/edit/${c.id}`) : undefined} onDelete={canEdit ? () => openConfirm(c) : undefined} />}
          sort={sort}
          onSort={changeSort}
          offset={0}
          limit={size}
          selection={{ selected, onChange: changeSelection }}
          empty={crmEmpty
            ? <EmptyRow title="No contacts yet" body="Add a contact to keep patient and family details together." action={<Link to="/crm/contacts/new" className="inline-flex h-10 items-center rounded-full bg-sun px-5 text-small font-semibold text-ink">Add contact</Link>} />
            : <EmptyRow title="No contacts match these filters" body="Try changing your search or filters." action={<button onClick={clear} className="h-10 rounded-full px-5 text-small font-semibold ring-1 ring-line hover:bg-mist">Clear filters</button>} />}
        /></div>
        {rows.length > 0 && <Pagination state={pg} noun="contact" scrollTo={tableRef} />}
      </>}
      {confirming && (() => {
        const leadCount = confirmLeads ? confirmLeads.length : confirming.leadCount || 0;
        const leadIds = (confirmLeads || []).slice(0, 3).map((lead) => lead.code);
        const contactName = fullName(confirming);
        return <ConfirmDialog
          title="Delete this contact?"
          body={leadCount
            ? <><p>{contactName} and their {leadCount} leads, with every task and activity on them, will be removed.</p>{leadIds.length > 0 && <p className="mt-2">{leadIds.join(", ")}{leadCount > leadIds.length ? ` and ${leadCount - leadIds.length} more` : ""}</p>}</>
            : <p>{contactName} will be removed from Contacts.</p>}
          confirmLabel="Delete contact"
          onConfirm={async () => {
            const target = confirming;
            setConfirming(null);
            try {
              await apiCall("delete", `/api/contacts/${target.id}`);
              setSelected((current) => { const next = new Set(current); next.delete(target.id); return next; });
              setToast({ message: "Contact deleted", onUndo: undoDelete([target.id]) });
            } catch (e) { setToast({ message: e.message }); }
            refresh();
          }}
          onCancel={() => setConfirming(null)}
        />;
      })()}
      {bulkDelete && <ConfirmDialog
        title={`Delete ${bulkDelete.ids.length} contacts?`}
        body={<p>This will also delete {bulkDelete.leads === null ? NO_DATA : bulkDelete.leads} {bulkDelete.leads === 1 ? "lead" : "leads"}, along with their tasks and activity.</p>}
        confirmLabel={`Delete ${bulkDelete.ids.length} contacts`}
        onCancel={() => setBulkDelete(null)}
        onConfirm={removeSelected}
      />}
      {convertOpen && <div className="olli fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-ink/35 p-4 font-sans text-ink" onMouseDown={() => setConvertOpen(false)}>
        <section role="dialog" aria-modal="true" aria-labelledby="convert-title" onMouseDown={(event) => event.stopPropagation()} className="flex w-[560px] max-w-full animate-nav-in flex-col gap-5 rounded-xl bg-paper p-7 shadow-lift">
          <div className="flex items-start justify-between gap-4"><div><h2 id="convert-title" className="text-h3 font-semibold">Create leads for {selectedContacts.length} contacts</h2><p className="mt-1 text-small text-muted">Set shared details for each new lead.</p></div><button type="button" onClick={() => setConvertOpen(false)} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist">×</button></div>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5 text-small font-medium">Source<SearchSelect value={source} onChange={setSource} options={SOURCES} /></label>
            <label className="flex flex-col gap-1.5 text-small font-medium">Status<SearchSelect value={leadStatus} onChange={setLeadStatus} options={leadStatuses.length ? leadStatuses.map((s) => ({ value: s.key_name, label: s.label })) : [{ value: "new", label: "New" }]} /></label>
            <label className="flex flex-col gap-1.5 text-small font-medium">Interested service <span className="text-caption font-normal text-muted">Optional</span><SearchSelect value={service} onChange={setService} options={service ? [service] : []} onCreate={setService} createLabel="Use" placeholder="No service selected" /></label>
            <label className="flex flex-col gap-1.5 text-small font-medium">Assign to<SearchSelect value={assignee} onChange={setAssignee} options={[UNASSIGNED, ...people.map((p) => p.name), RULES]} searchable /></label>
            <label className="flex flex-col gap-1.5 text-small font-medium">Priority<select value={priority} onChange={(event) => setPriority(event.target.value)} className={inputCls}>{PRIORITIES.map((value) => <option key={value}>{value}</option>)}</select></label>
          </div>
          {selectedOpenLeadCount > 0 && <div className="flex flex-col gap-3 rounded-sm bg-mist p-4"><p className="text-small text-muted">{selectedOpenLeadCount} selected {selectedOpenLeadCount === 1 ? "contact already has" : "contacts already have"} an open lead.</p><Checkbox id="skip-open-leads" checked={skipOpenLeads} onChange={setSkipOpenLeads}>Skip contacts who already have an open lead ({selectedOpenLeadCount})</Checkbox></div>}
          <div className="flex justify-end gap-2"><button type="button" onClick={() => setConvertOpen(false)} className="flex h-11 items-center rounded-full px-5 text-small font-semibold hover:bg-mist">Cancel</button><button type="button" onClick={createLeads} disabled={contactsToConvert.length === 0 || creating || openLeadsBy === null} className="flex h-11 items-center rounded-full bg-sun px-5 text-small font-semibold text-ink hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50">{openLeadsBy === null ? "Checking open leads…" : `Create ${contactsToConvert.length} leads`}</button></div>
        </section>
      </div>}
      {importOpen && <ImportDialog kind="contacts" initialStep="upload" onClose={() => setImportOpen(false)} onDone={({ added, updated, ids }) => { setImportOpen(false); setSelected(new Set((ids || []).map((id) => (Number.isNaN(Number(id)) ? id : Number(id))))); setToast({ message: `${added} contacts imported${updated ? `, ${updated} updated` : ""}` }); refresh(); }} />}
      {toast && <UndoToast message={toast.message} onUndo={toast.onUndo} onDone={() => setToast(null)} />}
      {createdToast && <LeadCreatedToast count={createdCount} onView={() => { setCreatedToast(false); navigate("/crm/leads"); }} onDone={() => setCreatedToast(false)} />}
      {deleteAll && <DeleteAllDialog state={deleteAll} shown={total} onCancel={() => setDeleteAll(null)} onConfirm={removeAllMatching} />}
      <BulkBar count={allMatching ? total : selected.size} noun="contact" onClear={clearSelection}>
        {canEdit && !allMatching && <button type="button" onClick={openConvert} className="flex h-10 items-center gap-2 rounded-full px-4 text-small font-semibold text-paper hover:bg-paper/10">Create leads</button>}
        <button type="button" disabled={exporting} onClick={() => exportContacts(allMatching ? "all" : "selected")} className="flex h-10 items-center gap-2 rounded-full px-4 text-small font-semibold text-paper hover:bg-paper/10 disabled:opacity-60"><Download size={16} strokeWidth={1.75} />{exporting ? "Exporting…" : "Export"}</button>
        {canBulkDelete && <button type="button" onClick={allMatching ? openDeleteAll : openBulkDelete} className="flex h-10 items-center gap-2 rounded-full px-4 text-small font-semibold text-paper hover:bg-paper/10"><Trash2 size={16} strokeWidth={1.75} />{allMatching ? "Delete all matching" : "Delete"}</button>}
      </BulkBar>
    </div>
  );
}

const CASCADE_LABEL = [["leads", "lead"], ["patients", "patient"], ["appointments", "appointment"], ["complaints", "complaint"], ["waba_conversations", "WhatsApp conversation"], ["social_conversations", "social inbox conversation"]];

/** "Delete all matching": the previewed count and what goes with it, then DELETE typed to confirm. */
function DeleteAllDialog({ state, shown, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    const key = (e) => e.key === "Escape" && !state.deleting && onCancel();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onCancel, state.deleting]);
  const linked = CASCADE_LABEL.map(([k, noun]) => [Number(state.cascade?.[k] || 0), noun]).filter(([n]) => n > 0);
  const ready = !state.loading && state.count > 0 && typed.trim() === "DELETE" && !state.deleting;
  return createPortal(
    <div className="olli fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-ink/35 p-4 font-sans text-ink" onMouseDown={() => !state.deleting && onCancel()}>
      <section role="alertdialog" aria-modal="true" aria-labelledby="delete-all-title" onMouseDown={(e) => e.stopPropagation()} className="flex w-[480px] max-w-full animate-nav-in flex-col gap-5 rounded-xl bg-paper p-7 shadow-lift">
        <div className="flex items-start gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger"><Trash2 size={18} strokeWidth={1.75} /></span><h2 id="delete-all-title" className="pt-1.5 text-h3 font-semibold">{state.loading ? "Delete all matching contacts?" : `Delete ${Number(state.count || 0).toLocaleString("en-IN")} contacts?`}</h2></div>
        {state.loading ? <InlineLoader size={20} /> : <div className="flex flex-col gap-3 text-small text-muted">
          {state.count === 0 && !state.error && <p>No contacts match these filters.</p>}
          {state.count > 0 && <p>Every contact that matches the current filters will be removed{linked.length ? ", along with" : "."}{linked.length > 0 && <> {linked.map(([n, noun]) => `${n.toLocaleString("en-IN")} ${noun}${n === 1 ? "" : "s"}`).join(", ")} linked to them.</>} This can't be undone from here.</p>}
          {state.count > 0 && shown !== undefined && state.count !== shown && <p className="font-medium text-danger">The list shows {Number(shown).toLocaleString("en-IN")} contacts, but {Number(state.count).toLocaleString("en-IN")} match on the server. Check the filters before you go on.</p>}
          {state.count > 0 && <label className="flex flex-col gap-1.5 text-caption font-semibold text-ink">Type DELETE to confirm<input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" aria-label="Type DELETE to confirm" className={`${inputCls} font-mono`} /></label>}
        </div>}
        {state.error && <p role="alert" className="rounded-sm bg-danger-soft px-3 py-2 text-small text-danger">{state.error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" disabled={state.deleting} onClick={onCancel} className="flex h-11 items-center rounded-full px-5 text-small font-semibold hover:bg-mist disabled:opacity-50">Cancel</button>
          <button type="button" disabled={!ready} onClick={onConfirm} className="flex h-11 items-center rounded-full bg-danger px-5 text-small font-semibold text-paper hover:brightness-95 disabled:cursor-not-allowed disabled:bg-mist disabled:text-muted">{state.deleting ? "Deleting…" : "Delete all"}</button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
